import React, { useEffect, useRef } from "react";
import styles from "./MainNavigation.module.css";
import {
  PatternlibHeaderPortal,
  PatternlibLanguagePicker,
  PatternlibTabs,
  PatternlibTabsItem,
} from "@liebherr/patternlib-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import AuthStatus from "../auth-status/AuthStatus";
import { pushToDataLayer } from "../../helpers/helpers";

const MainNavigation = ({ activeTab, padding, profile, isDemoUser }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const mainNavigationRef = useRef();
  const languagePickerRef = useRef();
  const location = useLocation();

  const languageChangedGA = () => {
    const eventName = "language_changed";
    const pagePath = location.pathname;
    const pageType = "home";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const language_new = language === "de-DE" ? "de" : "en";
    const language_old = language === "de-DE" ? "en" : "de";
    let props = { pagePath, pageType, language, language_old, language_new };

    pushToDataLayer(eventName, props);
  };

  const navigationClickGA = (navigationArea, clickedElement) => {
    const eventName = "navigation_click";
    const pagePath = location.pathname;
    const pageType = "home";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const navigation_area = navigationArea;
    const clicked_element = clickedElement;
    let props = {
      pagePath,
      pageType,
      language,
      navigation_area,
      clicked_element,
    };

    pushToDataLayer(eventName, props);
  };

  useEffect(() => {
    const ref = mainNavigationRef.current;
    const onClickListener = () => {
      navigate("/");
    };

    const onTabClickListener = (e) => {
      const tabId = e.detail.itemId;
      switch (tabId) {
        case 0:
          navigate("/");
          break;
        case 1:
          navigate("/myconfigs");
          break;
        default:
          navigate("/");
      }
    };

    // attach the event to the component
    ref.addEventListener("lhClickBrandLogo", onClickListener, false);
    ref.addEventListener("lhTabClick", onTabClickListener, false);

    // remove event on component unmount
    return () => {
      ref.removeEventListener("lhClickBrandLogo", onClickListener, true);
      ref.addEventListener("lhTabClick", onTabClickListener, true);
    };
  }, [mainNavigationRef]);

  useEffect(() => {
    if (!isDemoUser) {
      const ref = languagePickerRef.current;
      const onSelectListener = (e) => {
        // set language
        i18n.changeLanguage(e.detail.selected);
        languageChangedGA();
        // force reload to load cmp script with selected language
        window.location.reload();
      };

      // attach the event to the component
      ref.addEventListener("lhSelect", onSelectListener, false);
      // remove event on component unmount
      return () => {
        ref.removeEventListener("lhSelect", onSelectListener, true);
      };
    }
  }, [languagePickerRef]);

  return (
    <PatternlibHeaderPortal
      portal-name={t("MainNavigation.portalName")}
      brand-logo="https://www.liebherr.com/media/global/img/logos/logo_ci_liebherr_fl.svg"
      padding={padding}
      sticky-by-default
      ref={mainNavigationRef}
    >
      <div slot="more" className={styles.more}>
        {!isDemoUser && (
          <PatternlibLanguagePicker
            options='[{"label": "English", "value": "en", "detail": "(US)"}, {"label": "Deutsch", "value": "de"}]'
            selected={i18n.resolvedLanguage}
            ref={languagePickerRef}
          ></PatternlibLanguagePicker>
        )}
        <AuthStatus profile={profile} isDemoUser={isDemoUser}></AuthStatus>
      </div>
      <PatternlibTabs slot="tabs">
        <PatternlibTabsItem
          active={activeTab === 0 ? true : false}
          label={t("MainNavigation.configurator")}
          onClick={() =>
            navigationClickGA(
              "navigation_header",
              t("MainNavigation.configurator")
            )
          }
        ></PatternlibTabsItem>
        <PatternlibTabsItem
          label={t("MainNavigation.myProjects")}
          active={activeTab === 1 ? true : false}
          onClick={() =>
            navigationClickGA(
              "navigation_header",
              t("MainNavigation.myProjects")
            )
          }
        ></PatternlibTabsItem>
      </PatternlibTabs>
    </PatternlibHeaderPortal>
  );
};

MainNavigation.propTypes = {
  activeTab: PropTypes.number,
  padding: PropTypes.string,
};

MainNavigation.defaultProps = {
  activeTab: 0,
  padding: "0 24px",
};

export default MainNavigation;
